import Vue from "vue";
import axios from "axios";
import JwtService from "@/core/services/jwt.service";

import {
  //enums
  DIALOG_TYPE_ENUM,
  //actions
  OPEN_DIALOG
} from "@/core/services/store/dialog.module";

import {
  //actions
  ADD_LOADING_DATA,
  REMOVE_LOADING_DATA
} from "@/core/services/store/globalLoading.module";

import { LOGOUT } from "@/core/services/store/auth.module";

import store from "@/core/services/store/index";
import i18n from "@/core/plugins/vue-i18n";
import { v4 as uuidv4 } from "uuid";

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {
  /**
   * Set the default HTTP request headers
   */
  setDefaultHeaders() {},

  init() {
    // Request interceptor for API calls
    axios.interceptors.request.use(
      async config => {
        // Add global loading
        if (!config.ignoreGlobalLoader) {
          await store
            .dispatch(ADD_LOADING_DATA, config.loadingUniqueId)
            .then(response => (config.loadingUniqueId = response));
        }

        const accessToken = JwtService.getAccessToken();
        if (accessToken) {
          config.headers.XAuthentication = `${accessToken}`;
        }

        return config;
      },
      error => Promise.reject(error)
    );

    axios.interceptors.response.use(
      async function (success) {
        store.dispatch(REMOVE_LOADING_DATA, success.config.loadingUniqueId);

        // Globally redirect to login (if needed)
        if (success.data.methodStatus === 2) {
          store.dispatch(LOGOUT);
          Promise.reject(success);
        }

        return success;
      },
      async function (error) {
        store.dispatch(REMOVE_LOADING_DATA, error?.config?.loadingUniqueId);

        // Open unhandled error dialog (if needed)
        if (
          !error?.config?.ignoreUnhandledExceptionAlert &&
          axios.isCancel(error) === false
        ) {
          store.dispatch(OPEN_DIALOG, {
            text: i18n.t("General.Messages.UnhandledException"),
            type: DIALOG_TYPE_ENUM.error
          });
        }

        return Promise.reject(error);
      }
    );

    axios.defaults.baseURL = `${Vue.prototype.$xSettings.backendUrl}/api`;
  },

  async query(resource, params) {
    this.setDefaultHeaders();

    return await axios.get(resource, params).catch(error => {
      throw new Error(`[ApiService] ${error}`);
    });
  },

  abort(loadingUniqueId, abortController) {
    store.dispatch(REMOVE_LOADING_DATA, loadingUniqueId);
    abortController?.abort();
  },

  isCancel(exception) {
    return axios.isCancel(exception);
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  async get(resource, slug = "") {
    this.setDefaultHeaders();

    return await axios.get(`${resource}/${slug}`).catch(error => {
      throw new Error(`[ApiService] ${error}`);
    });
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params, config = {}) {
    this.setDefaultHeaders();

    const abortController = new AbortController();
    config.signal = abortController.signal;
    config.loadingUniqueId = uuidv4();

    return {
      result: axios.post(`${resource}`, params, config),
      loadingUniqueId: config.loadingUniqueId,
      abortController
    };
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  async update(resource, slug, params) {
    this.setDefaultHeaders();

    return await axios.put(`${resource}/${slug}`, params);
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  async put(resource, params) {
    this.setDefaultHeaders();

    return await axios.put(`${resource}`, params);
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  async delete(resource) {
    this.setDefaultHeaders();

    return await axios.delete(resource).catch(error => {
      throw new Error(`[ApiService] ${error}`);
    });
  },

  /**
   * Send the PATCH HTTP request
   * @param resource
   * @returns {*}
   */
  async patch(resource) {
    this.setDefaultHeaders();

    return await axios.patch(resource).catch(error => {
      throw new Error(`[ApiService] ${error}`);
    });
  }
};

export default ApiService;
